/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-lines-per-function */
import { type Layer, type NormalLayerSpec, defineNormalLayer } from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { type Option as TableOption } from '../../../ui/table/Table';
import { type TreeTableData } from '../../../ui/table/tables/TreeTable';
import * as tableConfig from '../../../ui/table/util/const';
import { sortFlatTree } from '../../../ui/table/util/helper';

export type CustomEvent = {
  data: {
    dataIndex: number;
    key: string;
    value: string | number;
  }[];
  selectedValue: string | number;
  selectedType: string;
};

type Event =
  | {
      type: string;
      data: CustomEvent;
    }
  | {
      type: string;
      page: number;
    }
  | {
      type: string;
      id: string;
    }
  | {
      type: string;
      isExpand: boolean;
    };

const visInfo: Layer['visInfo'] = {
  id: 'multiTable',
  name_en: 'MultiTable',
  name_zh: '多维表格',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    rows: {
      itemType: [DEFINE.STR],
      category: DEFINE.ARRAY
    },
    columns: {
      itemType: [DEFINE.STR],
      category: DEFINE.ARRAY
    }
  }
};

export interface Spec extends NormalLayerSpec {
  /** 使用的数据集索引，默认为 0 */
  dataIndex?: number;
  encoding: {
    rows?: string[];
    columns?: string[];
  };
  customEvent?: (event: Event) => void;
  isTreeTable?: boolean;
  maxHeight?: number;
  width?: number;
  columnsNum?: number;
  pageSize?: number;
  currentPage?: number;
}

export const layer = defineNormalLayer<Spec>({
  type: visInfo.id,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, ui, hook, token } = api;
    const {
      dataIndex = 0,
      encoding = {},
      customEvent,
      columnsNum = tableConfig.defaultSplitNum,
      maxHeight = tableConfig.maxHeight,
      width = tableConfig.width,
      pageSize = tableConfig.pageSize,
      currentPage = tableConfig.currentPage,
      isTreeTable = false
    } = spec;
    const { columns = [], rows = [] } = encoding;
    const pagination = {
      pageSize,
      currentPage
    };
    const tableStyle = {
      maxHeight,
      width
    };
    const defaultOpt = {
      formatData: api.hook.formatDataNumberValue,
      token,
      tableStyle
    };
    const data = dataArray[dataIndex].values;
    const dataMetaInfo = hook.parseDataMetaInfo(dataArray[dataIndex]);
    let dataset: TableOption & {
      columnsNum?: number;
      isTree?: boolean;
    };
    let multiTableIns:
      | InstanceType<typeof ui.HorizontalTable>
      | InstanceType<typeof ui.TreeTable>
      | InstanceType<typeof ui.MultiTableX>
      | InstanceType<typeof ui.MultiTableY>
      | InstanceType<typeof ui.VerticalTable>
      | undefined;

    // 横向一维表格
    if (data.length === 1 && !columns.length && rows.length) {
      if (columnsNum % 2 !== 0) {
        throw new Error('Please input an event-numbered column');
      }

      dataset = {
        data,
        columns,
        rows,
        columnsNum,
        dataMetaInfo
      };

      multiTableIns = new ui.HorizontalTable($dom, {
        option: dataset,
        ...defaultOpt
      });
    }

    // 纵向一维表格
    if (columns.length && !rows.length) {
      dataset = {
        data,
        columns,
        rows,
        dataMetaInfo
      };

      multiTableIns = new ui.VerticalTable($dom, {
        option: dataset,
        pagination,
        ...defaultOpt
      });
    }

    // 第一列为额外表头的多维表格
    if (columns.length > 1 && rows.length === 1) {
      const { rowsData } = util.dataEncoding.encoding(data.slice(), {
        rowsData: {
          field: rows[0],
          transforms: [{ type: 'sort', order: 'ascending' }]
        }
      });
      const transformData = util.dataTransform.transform(data.slice(), [
        { type: 'sort', field: rows[0], order: 'descending' }
      ]);
      dataset = {
        data: transformData as TableOption['data'],
        columns,
        rows: rowsData as TableOption['rows'],
        dataMetaInfo
      };

      multiTableIns = new ui.MultiTableX($dom, {
        option: dataset,
        pagination,
        ...defaultOpt
      });
    }

    // 第一行为额外表头的多维表格
    if (columns.length === 1 && rows.length > 1 && !isTreeTable) {
      const { columnsData } = util.dataEncoding.encoding(data.slice(), {
        columnsData: {
          field: columns[0],
          transforms: [{ type: 'sort', order: 'descending' }]
        }
      });
      columnsData.unshift(columns[0]);
      const transformData = util.dataTransform.transform(data.slice(), [
        { type: 'sort', field: columns[0], order: 'descending' }
      ]);
      dataset = {
        data: transformData as TableOption['data'],
        columns: columnsData as TableOption['columns'],
        rows,
        dataMetaInfo
      };

      multiTableIns = new ui.MultiTableY($dom, {
        option: dataset,
        pagination,
        ...defaultOpt
      });
    }

    // 树状表格
    if (isTreeTable) {
      const firstData = (data as unknown as TreeTableData[])[0];
      const sortedData = sortFlatTree(firstData);
      const treeTableKeys = Object.keys(sortedData);
      const treeTableColumns = treeTableKeys.filter(key => sortedData[key].columns);
      const treeTableRows = treeTableKeys.filter(key => sortedData[key].rows);
      const transformData = (data as unknown as TreeTableData[]).slice().sort((a, b) => {
        return (
          new Date(b[treeTableColumns[0]].value).getTime() -
          new Date(a[treeTableColumns[0]].value).getTime()
        );
      });
      const columnsData = transformData.map(item => {
        return item[treeTableColumns[0]].value;
      });
      columnsData.unshift(treeTableColumns[0]);
      dataset = {
        data: transformData as TableOption['data'],
        columns: columnsData as unknown as string[],
        rows: treeTableRows,
        isTree: true,
        dataMetaInfo
      };
      multiTableIns = new ui.TreeTable($dom, {
        option: dataset,
        pagination,
        ...defaultOpt
      });
    }

    function onCustomEvent(event: Event) {
      if (customEvent) {
        customEvent(event);
      }
    }

    // * -------------------------- Table
    if (multiTableIns) {
      multiTableIns.on('cell:selected', event => {
        onCustomEvent(event);
      });

      multiTableIns.on('page:changed', event => {
        onCustomEvent(event);
      });

      multiTableIns.on('tree:selected', event => {
        onCustomEvent(event);
      });

      multiTableIns.on('cell:expand', event => {
        onCustomEvent(event);
      });
    }

    // eslint-disable-next-line consistent-return
    return () => {
      // eslint-disable-next-line no-param-reassign
      $dom.innerHTML = '';
      multiTableIns?.destroy?.();
    };
  }
});
